import { Button, type ButtonVariant } from "@albertine/design-system/components/Button";
import { motion } from "motion/react";
import type React from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "../utils/cn.util";

type Props = {
    children: React.ReactNode;
    revealTriggerRef?: React.RefObject<HTMLElement | null>;
    hideTriggerRef?: React.RefObject<HTMLElement | null>;
    insideContentArea?: boolean;
    /** Fade in place. Do not slide or resize the button. */
    opacityOnly?: boolean;
    /** Extra scroll, in px, before the footer hides the button. */
    hideTriggerOffsetPx?: number;
    background?: string;
    onClick: () => void;
    asLink?: boolean;
    href?: string;
    variant?: ButtonVariant;
};

const SM_MD_MEDIA_QUERY = "(min-width: 720px)";
const LABEL_ELLIPSIS = "…";
const LABEL_WIDTH_BUFFER_PX = 8;

const visibilityTransition = {
    duration: 0.25,
    ease: [0.65, 0, 0.35, 1] as const,
};

function getViewportHalfWidth() {
    return window.innerWidth * 0.5;
}

function useIsSmMdUp() {
    const [isSmMdUp, setIsSmMdUp] = useState(
        () =>
            typeof window !== "undefined" &&
            window.matchMedia(SM_MD_MEDIA_QUERY).matches,
    );

    useEffect(function observeSmMdBreakpoint() {
        const mediaQuery = window.matchMedia(SM_MD_MEDIA_QUERY);
        const onChange = (event: MediaQueryListEvent) => {
            setIsSmMdUp(event.matches);
        };

        mediaQuery.addEventListener("change", onChange);
        return () => mediaQuery.removeEventListener("change", onChange);
    }, []);

    return isSmMdUp;
}

export function FloatingCTAButton({
    children,
    revealTriggerRef,
    hideTriggerRef,
    insideContentArea = true,
    opacityOnly = false,
    hideTriggerOffsetPx = 0,
    background = "var(--color-background-booking-quaternary)",
    onClick,
    asLink = false,
    href,
    variant = "primary",
}: Props) {
    const [hasEnteredView, setHasEnteredView] = useState(false);
    const [isHiddenByTrigger, setIsHiddenByTrigger] = useState(false);
    const [animatedWidth, setAnimatedWidth] = useState<number | null>(null);
    const [isWidthMeasured, setIsWidthMeasured] = useState(false);
    const labelText = typeof children === "string" ? children : null;
    const [displayLabel, setDisplayLabel] = useState(labelText);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const expandedWidthRef = useRef<number | null>(null);
    const isSmMdUp = useIsSmMdUp();

    useEffect(
        function observeRevealTrigger() {
            if (!insideContentArea) {
                setHasEnteredView(true);
                return;
            }

            const target = revealTriggerRef?.current ?? sentinelRef.current;
            if (!target) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setHasEnteredView(true);
                        return;
                    }
                    const rootBottom = entry.rootBounds?.bottom ?? window.innerHeight;
                    if (entry.boundingClientRect.top >= rootBottom) {
                        setHasEnteredView(false);
                    }
                },
                { rootMargin: "0px 0px -72px 0px" },
            );

            observer.observe(target);
            return () => observer.disconnect();
        },
        [insideContentArea, revealTriggerRef],
    );

    useLayoutEffect(
        function observeHideTrigger() {
            const element = hideTriggerRef?.current;
            if (!element) return;

            const observer = new IntersectionObserver(
                ([entry]) => {
                    setIsHiddenByTrigger(entry.isIntersecting);
                },
                {
                    threshold: 0.1,
                    rootMargin: `0px 0px -${hideTriggerOffsetPx}px 0px`,
                },
            );

            observer.observe(element);
            return () => observer.disconnect();
        },
        [hideTriggerRef, hideTriggerOffsetPx],
    );

    const isHidden = !hasEnteredView || isHiddenByTrigger;
    const shouldUseFixedArticleLayout = !insideContentArea;
    const shouldAnimateMobileWidth =
        !opacityOnly && shouldUseFixedArticleLayout && !isSmMdUp;

    useLayoutEffect(
        function resetWidthMeasurementOnBreakpointChange() {
            if (!shouldUseFixedArticleLayout) return;

            expandedWidthRef.current = null;
            setAnimatedWidth(null);
            setIsWidthMeasured(false);

            // Desktop uses CSS `w-fit`; mobile remeasures on next visible frame.
            if (!isSmMdUp) {
                return;
            }
        },
        [isSmMdUp, shouldUseFixedArticleLayout],
    );

    useLayoutEffect(
        function updateAnimatedWidthForVisibility() {
            if (!shouldAnimateMobileWidth) return;

            if (isHidden) {
                if (contentRef.current) {
                    const currentWidth = contentRef.current.offsetWidth;
                    const collapsedWidth = getViewportHalfWidth();

                    if (currentWidth > collapsedWidth * 1.05) {
                        expandedWidthRef.current = currentWidth;
                    }
                }

                setAnimatedWidth(getViewportHalfWidth());
                return;
            }

            if (expandedWidthRef.current !== null) {
                setAnimatedWidth(expandedWidthRef.current);
            }
        },
        [isHidden, shouldAnimateMobileWidth],
    );

    useLayoutEffect(
        function measureInitialExpandedWidth() {
            if (
                !shouldAnimateMobileWidth ||
                isHidden ||
                expandedWidthRef.current !== null
            )
                return;

            const element = contentRef.current;
            if (!element) return;

            const width = element.offsetWidth;
            if (width <= 0) return;

            expandedWidthRef.current = width;
            setAnimatedWidth(width);
            setIsWidthMeasured(true);
        },
        [shouldAnimateMobileWidth, isHidden],
    );

    useEffect(
        function remeasureExpandedWidthOnResize() {
            if (!shouldAnimateMobileWidth || isHidden) return;

            const onResize = () => {
                if (!contentRef.current || isHidden) return;

                setIsWidthMeasured(false);

                requestAnimationFrame(() => {
                    if (!contentRef.current || isHidden) return;

                    const width = contentRef.current.offsetWidth;
                    if (width <= 0) return;

                    expandedWidthRef.current = width;
                    setAnimatedWidth(width);
                    setIsWidthMeasured(true);
                });
            };

            window.addEventListener("resize", onResize);
            return () => window.removeEventListener("resize", onResize);
        },
        [shouldAnimateMobileWidth, isHidden],
    );

    useLayoutEffect(
        function truncateQuotedLabelToFit() {
            if (labelText === null) {
                setDisplayLabel(null);
                return;
            }

            const fullLabel = labelText;

            function update() {
                const button =
                    contentRef.current?.querySelector<HTMLElement>("button, a");
                const shellWidth =
                    shouldAnimateMobileWidth && animatedWidth != null
                        ? animatedWidth
                        : !shouldUseFixedArticleLayout && isSmMdUp
                          ? getDesktopContentMaxWidth()
                          : undefined;

                setDisplayLabel(
                    button
                        ? truncateQuotedLabel(fullLabel, button, shellWidth)
                        : fullLabel,
                );
            }

            update();
            window.addEventListener("resize", update);
            return () => window.removeEventListener("resize", update);
        },
        [
            isSmMdUp,
            labelText,
            animatedWidth,
            shouldAnimateMobileWidth,
            shouldUseFixedArticleLayout,
        ],
    );

    const footerAnimate = opacityOnly
        ? { opacity: isHidden ? 0 : 1 }
        : {
              y: isHidden ? "100%" : 0,
              ...(shouldAnimateMobileWidth && animatedWidth !== null
                  ? { width: animatedWidth }
                  : {}),
          };

    const footerWidthClasses = shouldUseFixedArticleLayout
        ? isSmMdUp
            ? cn("w-fit", maxFooterWidthClasses)
            : shouldAnimateMobileWidth && isWidthMeasured
              ? undefined
              : mobileFooterWidthClasses
        : undefined;

    const contentWidthClasses = shouldUseFixedArticleLayout
        ? isSmMdUp
            ? cn("w-fit", maxFooterWidthClasses)
            : mobileFooterWidthClasses
        : stickyContentShellWidthClasses;

    const footer = (
        <motion.footer
            initial={false}
            animate={footerAnimate}
            transition={visibilityTransition}
            className={cn(
                shouldUseFixedArticleLayout ? fixedFooterClasses : stickyFooterClasses,
                footerWidthClasses,
            )}
            style={
                {
                    pointerEvents: isHidden ? "none" : "auto",
                    transformOrigin: "50% 100%",
                    "--sticky-footer-background": background,
                } as React.CSSProperties
            }
        >
            <div
                ref={contentRef}
                className={cn(contentShellClasses, contentWidthClasses)}
            >
                <motion.div
                    aria-hidden="true"
                    animate={{ opacity: isHidden ? 0 : 1 }}
                    transition={visibilityTransition}
                    className={backdropClasses}
                />
                <motion.div
                    animate={{ opacity: isHidden ? 0 : 1 }}
                    transition={visibilityTransition}
                >
                    <Button
                        className="z-1 w-full max-w-full sm-md:w-max! sm-md:max-w-[512px]! sm-md:[&>span]:w-max!"
                        variant={variant}
                        onPress={onClick}
                        as={asLink ? "a" : "button"}
                        href={asLink ? href : undefined}
                        title={labelText ?? undefined}
                        aria-label={
                            labelText !== null && displayLabel !== labelText
                                ? labelText
                                : undefined
                        }
                    >
                        {displayLabel ?? children}
                    </Button>
                </motion.div>
            </div>
        </motion.footer>
    );

    return (
        <>
            <div ref={sentinelRef} aria-hidden="true" className="h-0 w-0" />
            {footer}
        </>
    );
}

const maxFooterWidthClasses = "max-w-[calc(100vw-var(--spacing-32))]";

const mobileFooterWidthClasses = cn("w-full", maxFooterWidthClasses);

const contentShellClasses = cn("relative z-30 overflow-clip rounded-button");

/** Ceiling for a long desktop label. Width itself stays the label's max-content. */
const DESKTOP_BUTTON_MAX_PX = 512;

const stickyDesktopMaxWidthClasses =
    "sm-md:max-w-[min(512px,calc(100%-var(--spacing-32)))]";

const stickyContentShellWidthClasses = cn(
    "w-full max-w-[calc(100%-var(--spacing-32))]",
    "sm-md:w-max",
    stickyDesktopMaxWidthClasses,
);

const fixedFooterClasses = cn(
    "pointer-events-auto fixed right-0 bottom-24 left-0 z-30",
    "mx-auto flex h-fit items-center justify-center",
);

const stickyFooterClasses = cn("sticky bottom-24 z-30", "flex w-full justify-center");

const backdropClasses = cn(
    "pointer-events-none",
    "absolute inset-0 -z-1",
    "h-full",
    "backdrop-blur-[12px]",
    "backdrop-brightness-105",
    "bg-(--sticky-footer-background)/60",
);

function readSpacing32() {
    const spacing = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue("--spacing-32"),
    );
    return Number.isFinite(spacing) ? spacing : 32;
}

function getMaxShellWidth() {
    return window.innerWidth - readSpacing32();
}

function getDesktopContentMaxWidth() {
    return Math.min(DESKTOP_BUTTON_MAX_PX, getMaxShellWidth());
}

let measureCanvas: HTMLCanvasElement | null = null;

/** Truncate inside quotes: `Book 'Bushmans Kloof…'` */
function truncateQuotedLabel(
    text: string,
    button: HTMLElement,
    shellWidth = getMaxShellWidth(),
): string {
    if (text.length === 0) return text;

    const style = getComputedStyle(button);
    const font =
        style.font.trim() ||
        `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const textTransform = style.textTransform;
    const letterSpacing = Number.parseFloat(style.letterSpacing) || 0;
    const maxWidth =
        shellWidth -
        Number.parseFloat(style.paddingLeft) -
        Number.parseFloat(style.paddingRight) -
        LABEL_WIDTH_BUFFER_PX;

    if (maxWidth <= 0) return text;

    if (!measureCanvas) {
        measureCanvas = document.createElement("canvas");
    }

    const context = measureCanvas.getContext("2d");

    if (context) {
        context.font = font;
    }

    const widthOf = (value: string) => {
        const rendered =
            textTransform === "uppercase"
                ? value.toLocaleUpperCase()
                : textTransform === "lowercase"
                  ? value.toLocaleLowerCase()
                  : value;
        const width = context?.measureText(rendered).width ?? rendered.length * 8;
        return letterSpacing !== 0 && rendered.length > 1
            ? width + letterSpacing * (rendered.length - 1)
            : width;
    };

    if (widthOf(text) <= maxWidth) return text;

    // Titles can contain apostrophes (`Evelyn's Table`), so the quoted span is
    // outermost-quote to outermost-quote.
    const startQuote = text.indexOf("'");
    const endQuote = text.lastIndexOf("'");
    if (startQuote < 0 || endQuote <= startQuote) {
        return fitLabelPrefix(text, "", widthOf, maxWidth);
    }

    const prefix = text.slice(0, startQuote + 1);
    const title = text.slice(startQuote + 1, endQuote);
    const suffix = text.slice(endQuote);
    const bare = `${prefix}${LABEL_ELLIPSIS}${suffix}`;

    if (widthOf(bare) > maxWidth) {
        return fitLabelPrefix(text, "", widthOf, maxWidth);
    }

    return fitLabelPrefix(title, prefix, widthOf, maxWidth, suffix);
}

function fitLabelPrefix(
    editable: string,
    prefix: string,
    widthOf: (value: string) => number,
    maxWidth: number,
    suffix = "",
): string {
    let best = `${prefix}${LABEL_ELLIPSIS}${suffix}`;
    let low = 0;
    let high = editable.length;

    while (low <= high) {
        const keep = (low + high) >> 1;
        const candidate = `${prefix}${editable.slice(0, keep)}${LABEL_ELLIPSIS}${suffix}`;
        if (widthOf(candidate) <= maxWidth) {
            best = candidate;
            low = keep + 1;
        } else {
            high = keep - 1;
        }
    }

    return best;
}
